export default function PortfolioSection() {
  const skills = [
    'AI Strategy', 'Machine Learning', 'Voice AI', 'NLP', 'LLMs',
    'Python', 'TensorFlow', 'OpenAI API', 'Cloud Architecture',
    'Product Management', 'Team Leadership', 'Patent Development',
  ];

  const achievements = [
    {
      icon: 'ri-patent-line',
      title: '40+ Patents Issued',
      desc: 'Inventor and co-inventor on over 40 technology patents at Cisco Systems, spanning AI, networking, and cloud infrastructure.',
    },
    {
      icon: 'ri-team-line',
      title: 'Led 75+ Engineers',
      desc: 'Built and scaled high-performing engineering teams across multiple product lines, geographies, and technology domains.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: '$2B+ Revenue Impact',
      desc: 'Directly contributed to products and strategic initiatives generating over $2 billion in cumulative revenue.',
    },
    {
      icon: 'ri-robot-line',
      title: 'VoiceCal AI System',
      desc: 'Built and launched VoiceCal — an AI-powered voice booking system enabling natural language appointment scheduling.',
    },
    {
      icon: 'ri-youtube-line',
      title: 'AdSkipper Extension',
      desc: 'Created AdSkipper, an AI-powered browser extension that intelligently detects and skips YouTube ads.',
    },
    {
      icon: 'ri-presentation-line',
      title: 'Keynote Speaker',
      desc: 'Recognized thought leader and speaker on AI innovation, digital transformation, and the future of enterprise technology.',
    },
  ];

  return (
    <section id="portfolio" className="py-24 px-6 lg:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-teal-50 text-teal-600 text-sm font-semibold rounded-full mb-4 tracking-wide uppercase">
            Portfolio
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Peter Gits — Innovation Portfolio
          </h2>
          <div className="w-16 h-1 bg-teal-500 mx-auto rounded-full mt-6"></div>
        </div>

        {/* Profile Card */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 mb-16 flex flex-col lg:flex-row items-center gap-10">
          <div className="flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/b0d28b98390413eddff47342c2e73859/449300a4e1f43ea71a0eb3cc8d20f6a6.jpeg"
              alt="Peter Gits Portfolio"
              className="w-48 h-48 rounded-2xl object-cover object-top border-4 border-teal-500/40 shadow-2xl"
            />
          </div>
          <div className="flex-1 text-center lg:text-left">
            <h3 className="text-3xl font-bold text-white mb-2">Peter Michael Gits</h3>
            <p className="text-teal-400 font-medium text-lg mb-4">AI Innovation Leader &amp; Fractional Head of AI</p>
            <p className="text-gray-400 leading-relaxed mb-6 max-w-2xl">
              A technology disruptor with 15+ years at Cisco Systems, Peter combines deep technical expertise with executive leadership to help businesses unlock the transformative power of AI. From strategy to execution, he delivers results that matter.
            </p>
            <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-6">
              {skills.map((skill) => (
                <span key={skill} className="px-3 py-1 bg-teal-500/20 text-teal-300 text-xs font-medium rounded-full border border-teal-500/30">
                  {skill}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
              <a
                href="https://gamma.app/docs/Peter-Gits-Innovative-Technology-Disruptor-vizar1xu377b0h0"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 bg-teal-500 hover:bg-teal-400 text-white text-sm font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-presentation-line"></i>
                View Full Presentation
              </a>
              <a
                href="https://www.linkedin.com/in/peter-michael-gits/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold rounded-full border border-white/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-linkedin-fill"></i>
                LinkedIn Profile
              </a>
            </div>
          </div>
        </div>

        {/* Achievement Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {achievements.map((a) => (
            <div key={a.title} className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all duration-200 hover:-translate-y-1">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-xl mb-4">
                <i className={`${a.icon} text-teal-600 text-2xl`}></i>
              </div>
              <h4 className="text-gray-900 font-bold text-lg mb-2">{a.title}</h4>
              <p className="text-gray-500 text-sm leading-relaxed">{a.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center bg-teal-50 rounded-2xl p-10 border border-teal-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">Ready to Work Together?</h3>
          <p className="text-gray-500 mb-6 max-w-xl mx-auto">
            Let&apos;s discuss how our team&apos;s expertise can help your business harness the power of AI.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-send-line mr-2"></i>Get In Touch
            </a>
            <a
              href="https://gamma.app/docs/Peter-Gits-Innovative-Technology-Disruptor-vizar1xu377b0h0"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-full border border-gray-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-presentation-line mr-2"></i>View Portfolio Deck
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
